<template>
  <div class="chat-input">
    <div class="input-container">
      <input
        v-model="newMessage"
        type="text"
        placeholder="Type your message..."
        class="message-input"
        :disabled="isLoading"
        @keyup.enter="sendMessageStream"
      />
      <button class="send-btn" :disabled="!newMessage.trim() || isLoading || !acceptedTerms" @click="sendMessageStream">
        <svg
          v-if="!isLoading"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="22" y1="2" x2="11" y2="13"></line>
          <polygon points="22,2 15,22 11,13 2,9"></polygon>
        </svg>
        <span v-else class="loading-spinner"></span>
      </button>
    </div>
    <div v-if="!acceptedTerms" class="accept-terms">
      <p>You must accept the terms below to use the KBuddy chat</p>
      <label>
        <input id="acceptTerms" v-model="acceptedTerms" type="checkbox" />
        I accept that metrics from the use of this chatbot will be used to help improve KBuddy. No personally
        identifiable information is collected.
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IChatMessage, IChatRequest, IChunk } from "../interfaces/chatBotInterfaces";
import { useChatBotStore } from "../stores/chatBotStore";

import { ref } from "vue";
import { v4 as createGuid } from "uuid";
import { chatBotApi } from "@/common";

const newMessage = ref("");
const isLoading = ref(false);
const acceptedTerms = ref(false);
const chatBotStore = useChatBotStore();

function copyStringWithoutReference(text: string) {
  return (" " + text).slice(1);
}

// async function sendMessage() {
//   if (!newMessage.value.trim() || isLoading.value) return;
//   const question = copyStringWithoutReference(newMessage.value.trim());
//   newMessage.value = "";

//   const userMessage: IChatMessage = {
//     id: Date.now().toString(),
//     type: "user",
//     text: question,
//     timestamp: new Date(),
//   };

//   chatBotStore.messages.push(userMessage);
//   isLoading.value = true;

//   const response = await ask(question);
//   const botMessage: IChatMessage = {
//     id: response.assistantMessageId?.toString(),
//     type: "bot",
//     text: response.answer,
//     timestamp: new Date(),
//   };
//   chatBotStore.messages.push(botMessage);
//   isLoading.value = false;
// }

// async function ask(question: string): Promise<IAnswer> {
//   try {
//     const request = {
//       sessionId: chatBotStore.sessionId,
//       message: question,
//       brand: "Safe",
//     } as IChatRequest;

//     const response = (await chatBotApi.ask(request)).data as IChatResponse;
//     chatBotStore.setSessionId(response.entity.data.sessionId);
//     console.log(chatBotStore.sessionId);

//     return {
//       sessionId: response.entity.data.sessionId,
//       assistantMessageId: response.entity.data.assistantMessageId,
//       answer: formatChatMessage({ message: response.entity.data.answer }) || "No response from bot.",
//       sources: [],
//     };
//   } catch (error) {
//     console.error("Error fetching token:", error);
//     return {
//       sessionId: chatBotStore.sessionId,
//       assistantMessageId: 0,
//       answer: "Error Error. Does not compute", // Rethrow the error after logging it
//       sources: [],
//     };
//   }
// }

async function sendMessageStream() {
  if (!newMessage.value.trim() || isLoading.value) return;
  const question = copyStringWithoutReference(newMessage.value.trim());
  newMessage.value = "";

  const userMessage: IChatMessage = {
    id: Date.now().toString(),
    type: "user",
    text: question,
    timestamp: new Date(),
  };

  //Add user message to chat before sending to API to avoid delay in rendering
  chatBotStore.messages.push(userMessage);
  isLoading.value = true;

  const request = {
    sessionId: chatBotStore.sessionId,
    message: question,
  } as IChatRequest;

  const response = await chatBotApi.askStream(request);

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    console.error("Unable to get response reader");
    isLoading.value = false;
    return;
  }

  let buffer: string | undefined = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const lines = buffer?.split("\n");
    buffer = lines?.pop(); // keep incomplete chunk

    for (const line of lines || []) {
      if (!line.startsWith("data:")) continue;

      const json = line.replace("data:", "").trim();

      if (!json) continue;

      const parsed = JSON.parse(json) as IChunk;
      if (parsed.sessionId) {
        chatBotStore.setSessionId(parsed.sessionId);
      }

      if (parsed.content) {
        const messageText = parsed.content.replace("**", "*"); // replace markdown bold with single asterisk for better formatting in Vue

        if (!chatBotStore.currentChatId) {
          const id = createGuid();
          chatBotStore.currentChatId = id;

          const botMessage: IChatMessage = {
            id: id,
            type: "bot",
            text: messageText,
            timestamp: new Date(),
          };
          chatBotStore.messages.push(botMessage);
        } else {
          // Append to existing bot message
          const currentBotMessage = chatBotStore.messages.find(msg => msg.id === chatBotStore.currentChatId);
          if (currentBotMessage) {
            currentBotMessage.text += messageText;
          }
        }
      }

      if (parsed.done) {
        console.log("stream finished");
        if (parsed.assistantMessageId) {
          console.log(`Updating message ID to ${parsed.assistantMessageId}`);
          const currentBotMessage = chatBotStore.messages.find(msg => msg.id === chatBotStore.currentChatId);
          if (currentBotMessage) {
            currentBotMessage.id = parsed.assistantMessageId.toString();
          }
        }

        chatBotStore.currentChatId = null;
        isLoading.value = false;
      }
    }
  }

  // try {
  //   while (true) {
  //     const { done, value } = await reader.read();
  //     if (done) {
  //       chatBotStore.currentChatId = null;
  //       break;
  //     }
  //     const data = decoder.decode(value, { stream: true });
  //     console.log("Received:", data);

  //     if (!chatBotStore.currentChatId) {
  //       const id = createGuid();
  //       chatBotStore.currentChatId = id;

  //       const botMessage: IChatMessage = {
  //         id: id,
  //         type: "bot",
  //         text: data,
  //         timestamp: new Date(),
  //       };
  //       chatBotStore.messages.push(botMessage);
  //     } else {
  //       // Append to existing bot message
  //       const currentBotMessage = chatBotStore.messages.find(msg => msg.id === chatBotStore.currentChatId);
  //       if (currentBotMessage) {
  //         currentBotMessage.text += data;
  //       }
  //     }
  //   }
  // } finally {
  //   isLoading.value = false;
  // }
}
</script>
